import type { UseMutationResult } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { type AssignableRider, useAssignableRiders } from "@/hooks/use-riders";
import {
  type DeliveryAssignmentResult,
  type DeliverySummary,
  useAssignRider,
} from "@/hooks/use-orders";

type AssignRiderMutation = UseMutationResult<
  DeliveryAssignmentResult,
  Error,
  { orderId: string; riderUserId: string }
>;

type AssignRiderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderDailyNumber: number;
  deliverySummary: DeliverySummary | null;
  onAssigned?: () => void;
  assignMutation?: AssignRiderMutation;
};

export function AssignRiderDialog({
  open,
  onOpenChange,
  orderId,
  orderDailyNumber,
  deliverySummary,
  onAssigned,
  assignMutation: assignMutationProp,
}: AssignRiderDialogProps) {
  const { t } = useTranslation();
  const ridersQuery = useAssignableRiders(orderId);
  const internalMutation = useAssignRider();
  const assignMutation: AssignRiderMutation = assignMutationProp ?? internalMutation;
  const [selectedRiderUserId, setSelectedRiderUserId] = useState<string | null>(null);

  const isReassignment = Boolean(deliverySummary?.riderId);
  const previousRiderId = deliverySummary?.riderId ?? null;
  const previousRiderName = deliverySummary?.riderName ?? null;

  useEffect(() => {
    if (!open) {
      setSelectedRiderUserId(null);
    }
  }, [open]);

  const riders = useMemo(() => ridersQuery.data ?? [], [ridersQuery.data]);

  const handleConfirm = async () => {
    if (!selectedRiderUserId) {
      return;
    }
    try {
      await assignMutation.mutateAsync({
        orderId,
        riderUserId: selectedRiderUserId,
      });
      toast.success(
        isReassignment
          ? t("orders.assign.reassigned", {
              defaultValue: "Rider reasignado al pedido #{{number}}",
              number: orderDailyNumber,
            })
          : t("orders.assign.assigned", {
              defaultValue: "Rider asignado al pedido #{{number}}",
              number: orderDailyNumber,
            }),
      );
      onAssigned?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Action failed");
    }
  };

  const description = isReassignment
    ? t("orders.assign.reassignDescription", {
        defaultValue:
          "Reasignar el pedido #{{number}}. El rider anterior ({{previous}}) será notificado.",
        number: orderDailyNumber,
        previous: previousRiderName,
      })
    : t("orders.assign.description", {
        defaultValue: "Elegí un rider disponible para el pedido #{{number}}.",
        number: orderDailyNumber,
      });

  const helpText = t("orders.assign.adminHelp", {
    defaultValue:
      "Solo aparecen los riders que están disponibles y que no rechazaron este pedido. La disponibilidad la maneja cada rider desde su app; la administración solo puede suspender o restaurar.",
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isReassignment
              ? t("orders.assign.reassignTitle", { defaultValue: "Reasignar rider" })
              : t("orders.assign.title", { defaultValue: "Asignar rider" })}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          <p className="text-muted-foreground text-xs" data-testid="orders.assign.adminHelp">
            {helpText}
          </p>
        </DialogHeader>

        {isReassignment && previousRiderName ? (
          <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">
              {t("orders.assign.currentRider", { defaultValue: "Rider actual" })}:
            </span>{" "}
            <span className="font-medium">{previousRiderName}</span>
          </div>
        ) : null}

        <div className="max-h-[320px] space-y-2 overflow-y-auto">
          {ridersQuery.isLoading ? (
            <div className="space-y-2" data-testid="assignable-riders-loading">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : ridersQuery.error ? (
            <p className="text-destructive text-sm">{(ridersQuery.error as Error).message}</p>
          ) : riders.length === 0 ? (
            <p
              className="rounded-md border border-border border-dashed p-4 text-center text-muted-foreground text-sm"
              data-testid="orders.assign.empty"
            >
              {t("orders.assign.empty", {
                defaultValue: "No hay riders disponibles.",
              })}
            </p>
          ) : (
            <ul className="space-y-1" data-testid="assignable-riders-list">
              {riders.map((rider) => (
                <AssignableRiderRow
                  key={rider.userId}
                  rider={rider}
                  selected={selectedRiderUserId === rider.userId}
                  isCurrent={previousRiderId === rider.userId}
                  onSelect={setSelectedRiderUserId}
                />
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", { defaultValue: "Cancelar" })}
          </Button>
          <Button
            type="button"
            disabled={
              !selectedRiderUserId ||
              selectedRiderUserId === previousRiderId ||
              assignMutation.isPending
            }
            onClick={handleConfirm}
          >
            {assignMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isReassignment ? (
              t("orders.assign.reassign", { defaultValue: "Reasignar" })
            ) : (
              t("orders.assign.confirm", { defaultValue: "Asignar" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignableRiderRow({
  rider,
  selected,
  isCurrent,
  onSelect,
}: {
  rider: AssignableRider;
  selected: boolean;
  isCurrent: boolean;
  onSelect: (userId: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <li>
      <button
        aria-pressed={selected}
        className={`flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors ${
          selected
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/40 hover:bg-muted/40"
        }`}
        data-testid={`assignable-rider-${rider.userId}`}
        onClick={() => onSelect(rider.userId)}
        type="button"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">{rider.name}</span>
            {isCurrent ? (
              <Badge variant="secondary">
                {t("orders.assign.currentBadge", { defaultValue: "Actual" })}
              </Badge>
            ) : null}
          </div>
          <p className="truncate text-muted-foreground text-xs">{rider.email}</p>
          {rider.vehicleType || rider.licensePlate ? (
            <p className="mt-1 text-muted-foreground text-xs">
              {rider.vehicleType ? (
                <span>
                  {t("orders.assign.vehicle", { defaultValue: "Vehículo" })}: {rider.vehicleType}
                </span>
              ) : null}
              {rider.vehicleType && rider.licensePlate ? " · " : ""}
              {rider.licensePlate ? (
                <span>
                  {t("orders.assign.plate", { defaultValue: "Patente" })}: {rider.licensePlate}
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      </button>
    </li>
  );
}
